import React, { useEffect, useState } from 'react';
import {
  isFileSystemAccessSupported,
  loadDirectoryHandle,
  saveDirectoryHandle,
  clearDirectoryHandle,
  ensureReadWritePermission,
} from '../lib/audioFolderStore';

interface AudioFolderPickerProps {
  /** Notified whenever the chosen folder changes (name, or null when cleared). */
  onChange?: (folderName: string | null) => void;
}

const AudioFolderPicker: React.FC<AudioFolderPickerProps> = ({ onChange }) => {
  const [supported] = useState<boolean>(isFileSystemAccessSupported());
  const [folderName, setFolderName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /* Restore a previously chosen folder on mount */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const handle = await loadDirectoryHandle();
        if (!cancelled && handle) {
          setFolderName(handle.name);
          onChange?.(handle.name);
        }
      } catch {
        /* ignore — user can re-pick */
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pickFolder = async () => {
    setError(null);
    try {
      // @ts-expect-error showDirectoryPicker is not yet in all TS DOM libs
      const handle: FileSystemDirectoryHandle = await window.showDirectoryPicker({
        id: 'knh-medscribe-recordings',
        mode: 'readwrite',
      });
      const granted = await ensureReadWritePermission(handle);
      if (!granted) {
        setError('Write permission was denied for that folder.');
        return;
      }
      await saveDirectoryHandle(handle);
      setFolderName(handle.name);
      onChange?.(handle.name);
    } catch (err) {
      // AbortError = user cancelled the picker — not an error worth showing
      if (err instanceof DOMException && err.name === 'AbortError') return;
      setError(err instanceof Error ? err.message : 'Could not select folder.');
    }
  };

  const clearFolder = async () => {
    setError(null);
    try {
      await clearDirectoryHandle();
    } finally {
      setFolderName(null);
      onChange?.(null);
    }
  };

  if (!supported) {
    return (
      <div
        style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: 12,
          color: '#92400E',
          background: '#FFFBEB',
          border: '1px solid #FDE68A',
          borderRadius: 8,
          padding: '8px 12px',
          marginBottom: 16,
        }}
      >
        This browser can’t auto-save to a folder. Recordings will download
        instead. Use Chrome or Edge for folder saving.
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 16 }}>
      <label
        style={{
          display: 'block',
          fontSize: 11,
          fontWeight: 600,
          color: '#5B6591',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          marginBottom: 6,
        }}
      >
        Auto-save folder
      </label>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '8px 12px',
          background: folderName ? '#F2FAF4' : '#F9FAFB',
          border: `1px solid ${folderName ? '#BBE7C6' : '#DEE2F1'}`,
          borderRadius: 8,
        }}
      >
        <i
          className={`fas ${folderName ? 'fa-folder-open' : 'fa-folder'}`}
          style={{ fontSize: 13, color: folderName ? '#16A34A' : '#9CA3AF' }}
        />
        <span
          style={{
            flex: 1,
            fontFamily: 'Inter, sans-serif',
            fontSize: 13,
            fontWeight: 500,
            color: folderName ? '#1F2332' : '#667085',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {folderName
            ? `Saving to “${folderName}”`
            : 'No folder selected — recordings will download'}
        </span>

        <button
          type="button"
          onClick={pickFolder}
          style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 12,
            fontWeight: 600,
            color: '#FFFFFF',
            background: 'linear-gradient(135deg, #5ab4e7 0%, #4a5585 100%)',
            border: 'none',
            borderRadius: 6,
            padding: '6px 12px',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          {folderName ? 'Change' : 'Choose folder'}
        </button>

        {folderName && (
          <button
            type="button"
            onClick={clearFolder}
            title="Clear folder"
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: 12,
              fontWeight: 600,
              color: '#5B6591',
              background: '#FFFFFF',
              border: '1px solid #DEE2F1',
              borderRadius: 6,
              padding: '6px 10px',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            Clear
          </button>
        )}
      </div>

      {error && (
        <p style={{ margin: '6px 2px 0', fontSize: 12, color: '#DC2626' }}>
          {error}
        </p>
      )}
    </div>
  );
};

export default AudioFolderPicker;
